import {
  Button,
  DatePicker,
  Form,
  Input,
  Radio,
  Checkbox,
  Spin,
} from "antd";
import { useEffect } from "react";
import dayjs from "dayjs";
import SignatureField from "../../common/SignatureField";
import {
  useCreateSubmitQuestionnaireMutation,
  useGetQuestionnaireDataQuery,
} from "../../redux/features/routeQuestionApi";
import { toast } from "react-toastify";

export default function StepQuestionnaire({
  formData,
  setFormData,
  visit_type_id,
  patientid,
  onNext,
  onBack
}) {
  const [form] = Form.useForm();
  const [createPatientData, { isLoading }] =
    useCreateSubmitQuestionnaireMutation();
  const { data: GetQuestionnaire, isLoading: isQuestionnaireLoading } =
    useGetQuestionnaireDataQuery(visit_type_id);

  // Keep phone fields as plain 10-digit text when data loads
  useEffect(() => {
    if (GetQuestionnaire?.data?.data && !isQuestionnaireLoading) {
      const phoneFields = GetQuestionnaire.data.data.filter(field => 
        field.notes?.toLowerCase().includes("phone number") ||
        field.notes?.toLowerCase().includes("mobile number")
      );

      phoneFields.forEach(field => {
        const currentValue = form.getFieldValue(field.entry_id);
        if (currentValue) {
          const digits = String(currentValue).replace(/\D/g, "").slice(-10);
          form.setFieldsValue({ [field.entry_id]: digits });
        }
      });
    }
  }, [GetQuestionnaire, isQuestionnaireLoading, form]);

  const handleSubmitQuestionnaire = async (values) => {

   const answers = Object.entries(values)
  .filter(([_, v]) => v !== undefined && v !== null && v !== "")
  .map(([entry_id, answer]) => {
      if (dayjs.isDayjs(answer)) {
        // format Date
        return {
          entry_id,
          answer: answer.format("MMM DD, YYYY"),
        };
      }
      // Check if this is a phone field and keep plain 10-digit value
      const field = GetQuestionnaire?.data?.data?.find(f => f.entry_id === entry_id);
      const isPhoneField = field && (
        field.notes?.toLowerCase().includes("phone number") ||
        field.notes?.toLowerCase().includes("mobile number")
      );
      
      if (isPhoneField && answer) {
        const digits = String(answer).replace(/\D/g, "").slice(0, 10);
        if (digits.length === 10) {
          return { entry_id, answer: digits };
        }
      }
      
      return { entry_id, answer };
    });

    // Get patient_id from prop or fallback to formData
    // Check multiple possible keys from the API response
    const patient_id = patientid || formData?.patientid || formData?.patient_id || formData?.patientId || formData?.data?.patientid || formData?.data?.patient_id;
    
  

    if (!patient_id) {
      console.error("WARNING: patient_id is missing! Cannot submit questionnaire without patient_id.");
      toast.error("Patient ID is missing. Please go back and complete patient information first.");
      return;
    }

    const payload = {
      template_id: GetQuestionnaire?.visit_type_info?.template_id,
      visit_type_id: GetQuestionnaire?.visit_type_info?.visit_type_id,
      patient_id: patient_id,
      is_submitted: true,
      answers,
    };

    console.log("Debug - payload being sent:", JSON.stringify(payload, null, 2));

    try {
      const res = await createPatientData(payload).unwrap();
          toast.success("Questionnaire submitted successfully");
      
      setFormData({ ...formData, ...res });
      onNext();
    } catch (err) {
      const apiMessage =
            err?.data?.message || err?.error || "Failed to save patient";
      
          console.error("Error creating patient:", apiMessage);
          toast.error(apiMessage);
    }
  };

  const handleFinish = async (values) => {
    await handleSubmitQuestionnaire(values);
  };

const renderField = (field) => {
  const requiredRule = field.is_mandatory
    ? [{ required: true, message: `Please enter/select ${field.notes}` }]
    : [];

  const isPhoneField =
    field.notes?.toLowerCase().includes("phone number") ||
    field.notes?.toLowerCase().includes("mobile number");

  const isDOBorTodayField =
    ["date of birth:"].includes(
      field.notes?.trim().toLowerCase()
    );
  const phoneValidator = (_, value) => {
    const digits = (value || "").replace(/\D/g, "");
    if (digits.length === 10) {
      return Promise.resolve();
    }
    if (digits.length > 10) {
      return Promise.reject(
        "Phone number must be exactly 10 digits"
      );
    }
    return Promise.reject(
      "Phone number must be 10 digits"
    );
  };

 const handlePhoneChange = (e, fieldEntryId) => {
  const digits = String(e.target.value || "").replace(/\D/g, "").slice(0, 10);
  form.setFieldsValue({ [fieldEntryId]: digits });
};
  if (field.notes_type === "Label") {
    return (
      <div
        key={field.entry_id}
        style={{
          fontWeight: field.label_style?.font_weight,
          textDecoration: field.label_style?.text_decoration,
          fontSize: field.label_style?.font_size,
          textAlign: field.label_style?.text_align,
          margin: "10px 0",
        }}
      >
        {field.notes}
      </div>
    );
  }

if (isDOBorTodayField) {
  return (
    <Form.Item
      key={field.entry_id}
      name={field.entry_id}
      label={field.notes}
      rules={[
        ...requiredRule,
        {
          pattern: /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-\d{4}$/,
          message: "Please enter a valid date in MM-DD-YYYY format",
        },
        {
          validator: (_, value) => {
            if (!value) return Promise.resolve();
            const [month, day, year] = value.split("-");
            const date = dayjs(`${year}-${month}-${day}`, "YYYY-MM-DD", true);
            if (!date.isValid()) {
              return Promise.reject("Invalid date");
            }
            if (date.isAfter(dayjs(), "day")) {
              return Promise.reject("Date of birth cannot be in the future");
            }
            return Promise.resolve();
          },
        },
      ]}
    >
      <Input
        placeholder="MM-DD-YYYY"
        maxLength={10}
        onChange={(e) => {
          let value = e.target.value.replace(/\D/g, "");
          if (value.length > 8) value = value.slice(0, 8);
          let formatted = value;
          if (value.length > 4) {
            formatted = value.replace(
              /^(\d{2})(\d{2})(\d{0,4}).*/,
              (_, m, d, y) => `${m}-${d}${y ? "-" + y : ""}`
            );
          } else if (value.length > 2) {
            formatted = value.replace(/^(\d{2})(\d{0,2})/, "$1-$2");
          }
          form.setFieldsValue({ [field.entry_id]: formatted });
        }}
      />
    </Form.Item>
  );
}


if (field.notes?.trim().toLowerCase() === "today's date") {
  form.setFieldsValue({
    [field.entry_id]: dayjs(),
  });

  return (
    <Form.Item
      key={field.entry_id}
      name={field.entry_id}
      label={field.notes}
      rules={requiredRule}
    >
      <DatePicker
        style={{ width: "100%" }}
        format="YYYY-MM-DD"
        // disabled
      />
    </Form.Item>
  );
}

  if (isPhoneField) {
    return (
      <Form.Item
        key={field.entry_id}
        name={field.entry_id}
        label={field.notes}
        normalize={(value) => {
          if (!value) return value;
          return String(value).replace(/\D/g, "").slice(0, 10);
        }}
        rules={[
          ...requiredRule,
          { validator: phoneValidator },
        ]}
      >
        <Input
          type="tel"
          inputMode="numeric"
          placeholder="XXXXXXXXXX"
          maxLength={10}
          onChange={(e) => handlePhoneChange(e, field.entry_id)}
          onPaste={(e) => {
            e.preventDefault();
            const pastedText = (e.clipboardData || window.clipboardData).getData("text");
            const currentValue = form.getFieldValue(field.entry_id) || "";
            const currentDigits = String(currentValue).replace(/\D/g, "");
            const pastedDigits = String(pastedText).replace(/\D/g, "");
            const remainingSlots = Math.max(0, 10 - currentDigits.length);
            const digitsToAdd = pastedDigits.slice(0, remainingSlots);
            if (digitsToAdd.length > 0) {
              form.setFieldsValue({ [field.entry_id]: (currentDigits + digitsToAdd).slice(0, 10) });
            }
          }}
          onKeyDown={(e) => {
            const currentValue = e.target.value || "";
            const digits = currentValue.replace(/\D/g, "");
            if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "Tab"].includes(e.key)) {
              return;
            }
            if (e.ctrlKey || e.metaKey) {
              return;
            }
            if (["Backspace", "Delete"].includes(e.key)) {
              return;
            }
            if (!/^\d$/.test(e.key)) {
              e.preventDefault();
              return;
            }
            if (digits.length >= 10) {
              e.preventDefault();
            }
          }}
          onBeforeInput={(e) => {
            const inputType = e.data;
            if (inputType && !/^\d$/.test(inputType)) {
              e.preventDefault();
              return false;
            }
          }}
          onInput={(e) => {
            const value = e.target.value;
            const digits = String(value).replace(/\D/g, "").slice(0, 10);
            if (digits !== value) {
              form.setFieldsValue({ [field.entry_id]: digits });
            }
          }}
        />
      </Form.Item>
    );
  }

  switch (field.notes_type) {
    case "Simple Question":
    case "Question":
      return (
        <Form.Item
          key={field.entry_id}
          name={field.entry_id}
          label={field.notes}
          rules={requiredRule}
        >
          <Input />
        </Form.Item>
      );

    case "Date":
      return (
        <Form.Item
          key={field.entry_id}
          name={field.entry_id}
          label={field.notes}
          rules={requiredRule}
        >
          <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
        </Form.Item>
      );

    case "Question with Fixed Options":
      return (
        <Form.Item
          key={field.entry_id}
          name={field.entry_id}
          label={field.notes}
          rules={requiredRule}
        >
          <Radio.Group>
            {field.options?.map((opt) => (
              <Radio key={opt} value={opt}>
                {opt}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      );

    case "Question with Options":
      return (
        <Form.Item
          key={field.entry_id}
          name={field.entry_id}
          label={field.notes}
          rules={requiredRule}
        >
          {field.is_multi_choice ? (
            <Checkbox.Group style={{ width: "100%" }}>
              {field.options?.map((opt) => (
                <Checkbox key={opt} value={opt}>
                  {opt}
                </Checkbox>
              ))}
            </Checkbox.Group>
          ) : (
            <Radio.Group>
              {field.options?.map((opt) => (
                <Radio key={opt} value={opt}>
                  {opt}
                </Radio>
              ))}
            </Radio.Group>
          )}
        </Form.Item>
      );

    case "Signature":
      return (
        <Form.Item
          key={field.entry_id}
          name={field.entry_id}
          label={field.notes}
          rules={requiredRule}
          valuePropName="value"
          getValueFromEvent={(val) => "check"}
        >
          <SignatureField />
        </Form.Item>
      );

      case "Rating Scale":
  const numbers = [];
  for (let i = field.from_scale; i <= field.to_scale; i++) {
    numbers.push(i);
  }

  return (
    <Form.Item
      key={field.entry_id}
      name={field.entry_id}
      label={field.notes}
      rules={requiredRule}
    >
      <Radio.Group>
        {numbers.map((num) => (
          <Radio key={num} value={num}>
            {num}
          </Radio>
        ))}
      </Radio.Group>
    </Form.Item>
  );
   
  case "Yes/No Question":
  return (
    <Form.Item
      key={field.entry_id}
      name={field.entry_id}
      label={field.notes}
      rules={requiredRule}
    >
      <Radio.Group>
        <Radio value="Yes">Yes</Radio>
        <Radio value="No">No</Radio>
      </Radio.Group>
    </Form.Item>
  );
  default:
      return null;
  }
  
};


  if (isQuestionnaireLoading) {
    return(
       <div
      style={{
        height: "200px",
        display: "flex",
        alignItems: "center", 
        justifyContent: "center",
      }}
    >
      <Spin size="large" />
    </div>
    )
  }


  return (
    <div className="step-patient-info">
      {/* <h2 className="form-title text-white">Your Information</h2> */}
      <h2 className="form-title text-white">{formData?.service?.visit_type}</h2>

      <Form
        form={form}
        layout="vertical"
        initialValues={formData}
        onFinish={handleFinish}
        className="form-container"
        scrollToFirstError   
      >
        {!isQuestionnaireLoading &&
          GetQuestionnaire?.data?.data?.map((field) => renderField(field))}

       <Form.Item>
  <div className="form-actions-row">
    <Button className="btn-css-class" onClick={onBack} block>
      Back
    </Button>
    <Button
      type="primary"
      className="btn-css"
      htmlType="submit"
      block
      loading={isLoading}
    >
      Next
    </Button>
  </div>
</Form.Item>

      </Form>
    </div>
  );
}
